import { Box, Flex, IconButton, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable, createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import isEmpty from 'lodash/isEmpty';
import { useHistory } from 'react-router-dom';
import { FORMAT_DATE, ModalType } from 'constants/common';
import { toast } from 'components/Toast';
import { useDeleteCategoryMutation, useSetBestCategoryMutation } from 'services/category';
import { formatDate } from 'utils/helpers';
import capitalize from 'lodash/capitalize';
import { formatImageUrl } from 'utils/url';
import AuthorizationComponentWrapper from 'components/AuthorizationComponentWrapper/AuthorizationComponentWrapper';
import { useUserState } from 'context/UserContext';

const SizeTable = ({ categoryData, handleUpdateCategory, refetch }) => {
  const history = useHistory();
  const [sorting, setSorting] = useState([]);
  const columnHelper = createColumnHelper();
  const setBestCategoryMutation = useSetBestCategoryMutation();
  const deleteCategoryMutation = useDeleteCategoryMutation();
  const handleRowClick = (ticket, type) => {
    handleUpdateCategory(ticket, type);
  };
  const { userInfo } = useUserState();

  const handleDeleteSize = async category => {
    const confirmDelete = window.confirm('Bạn có chắc chắn muốn xóa danh mục này không?');
    if (!confirmDelete) {
      return;
    }
    deleteCategoryMutation.mutate(
      { id: category?._id },
      {
        onSuccess: () => {
          toast.showMessageSuccess('Xóa danh mục thành công');
          refetch?.();
        },
        onError: () => {
          toast.showMessageError('Xóa danh mục không thành công');
          refetch?.();
        },
      }
    );
  };

  const handleBestCategory = async category => {
    const actionText = category.isBest ? 'Xóa' : 'Thêm';
    const confirmMessage = window.confirm(`Bạn có chắc chắn muốn cài đặt danh mục ${category.title} là danh mục tốt nhất?`);
    if (!confirmMessage) {
      return;
    }
    setBestCategoryMutation.mutate(
      { id: category._id },
      {
        onSuccess: () => {
          const successMessage = `${actionText} danh mục ${category.title} là danh mục tốt nhất thành công`;
          toast.showMessageSuccess(successMessage);
          refetch?.();
        },
        onError: () => {
          const errorMessage = `${actionText} danh mục ${category.title} là danh mục tốt nhất không thành công`;
          toast.showMessageError(errorMessage);
          refetch?.();
        },
      }
    );
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('title', {
        header: 'Tên',
        cell: info => capitalize(info.getValue()),
      }),

      columnHelper.accessor('description', {
        header: 'Mô tả',
        cell: info => info.getValue(),
      }),

      columnHelper.accessor('thumbnail', {
        header: 'Ảnh',
        cell: info => {
          const thumbnailUrl = info.row.original.thumbnail ? formatImageUrl?.(info.row.original.thumbnail) : '';
          return thumbnailUrl ? <img src={thumbnailUrl} style={{ width: '80px', height: '80px', objectFit: 'contain' }} /> : null;
        },
      }),

      columnHelper.accessor('createdAt', {
        header: 'Ngày tạo',
        cell: info => <Text whiteSpace={'nowrap'}>{formatDate(info.row.original.createdAt, FORMAT_DATE)}</Text>,
      }),

      columnHelper.accessor('action', {
        header: 'Thao tác',
        cell: info => (
          <AuthorizationComponentWrapper groupPermission={userInfo?.permissions} groupName={'UPDATE_PRODUCT_CATEGORY'}>
            <Flex alignItems="center" gap={1}>
              <IconButton
                bg="transparent"
                onClick={() => {
                  handleRowClick(info?.row?.original, ModalType.Add);
                }}
              >
                <EditIcon cursor="pointer" boxSize={4} />
              </IconButton>
              {/* <IconButton
              bg="transparent"
              onClick={() => {
                history.push(`/admin/category/${info?.row?.original?._id}/size`);
                }}
                >
              <TbTextResize size={16} />
              </IconButton>
              <IconButton
              bg="transparent"
              onClick={() => {
                history.push(`/admin/category/${info?.row?.original?._id}/colors`);
                }}
                >
                <MdOutlineColorLens size={16} />
                </IconButton> */}
              <IconButton
                bg="transparent"
                onClick={() => {
                  handleDeleteSize(info?.row?.original);
                }}
              >
                <DeleteIcon color="red.400" boxSize={4} />
              </IconButton>
            </Flex>
          </AuthorizationComponentWrapper>
        ),
      }),
    ],
    [categoryData]
  );

  const table = useReactTable({
    data: categoryData || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <Table>
      <Thead>
        {table.getHeaderGroups().map(headerGroup => (
          <Tr key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <Th key={header.id} w="120px">
                {header.isPlaceholder ? null : (
                  <Box cursor={header.column.getCanSort() ? 'pointer' : 'default'} onClick={header.column.getToggleSortingHandler()}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      asc: ' 🔼',
                      desc: ' 🔽',
                    }[header.column.getIsSorted()] ?? null}
                  </Box>
                )}
              </Th>
            ))}
          </Tr>
        ))}
      </Thead>
      <Tbody>
        {isEmpty(table.getRowModel().rows) ? (
          <Tr>
            <Td textAlign="center" colSpan={4}>
              Không có dữ liệu
            </Td>
          </Tr>
        ) : (
          table.getRowModel().rows.map(row => (
            <Tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>
              ))}
            </Tr>
          ))
        )}
      </Tbody>
    </Table>
  );
};

export default SizeTable;
